import type { RemoteSyncInventory } from './syncTransport'
import type { Note } from '../domain/note'

export interface LocalRestoreInventory {
  noteIds: string[]
  assetIds: string[]
}

export interface RestoreInventoryReconciliation {
  matches: boolean
  noteIdsMatch: boolean
  assetIdsMatch: boolean
  missingAssetObjectCount: number
  localNoteCount: number
  remoteNoteCount: number
  localAssetCount: number
  remoteAssetCount: number
}

export function synchronizedTrashNotesMissingFromRemote(
  localNotes: readonly Note[],
  remoteNoteIds: readonly string[],
): string[] | null {
  const remoteIds = new Set(remoteNoteIds)
  const localIds = new Set(localNotes.map((note) => note.id))
  if (remoteNoteIds.some((id) => !localIds.has(id))) return null

  const localOnlyNotes = localNotes.filter((note) => !remoteIds.has(note.id))
  if (localOnlyNotes.some((note) => (
    note.status !== 'trash'
    || note.localSyncState !== 'synced'
    || (note.assetIds?.length ?? 0) > 0
  ))) return null

  return normalizedIds(localOnlyNotes.map((note) => note.id))
}

function normalizedIds(ids: readonly string[]): string[] {
  return [...new Set(ids)].sort()
}

function equalIds(left: readonly string[], right: readonly string[]): boolean {
  const normalizedLeft = normalizedIds(left)
  const normalizedRight = normalizedIds(right)
  return normalizedLeft.length === normalizedRight.length
    && normalizedLeft.every((id, index) => id === normalizedRight[index])
}

export function reconcileRestoreInventory(
  local: LocalRestoreInventory,
  remote: RemoteSyncInventory,
): RestoreInventoryReconciliation {
  const noteIdsMatch = equalIds(local.noteIds, remote.noteIds)
  const assetIdsMatch = equalIds(local.assetIds, remote.assetIds)
  const missingAssetObjectCount = normalizedIds(remote.missingAssetObjectIds).length

  return {
    matches: noteIdsMatch && assetIdsMatch && missingAssetObjectCount === 0,
    noteIdsMatch,
    assetIdsMatch,
    missingAssetObjectCount,
    localNoteCount: normalizedIds(local.noteIds).length,
    remoteNoteCount: normalizedIds(remote.noteIds).length,
    localAssetCount: normalizedIds(local.assetIds).length,
    remoteAssetCount: normalizedIds(remote.assetIds).length,
  }
}
